/**
 * Verificacion de integridad de la base (Req. 18).
 *
 * Responde una sola pregunta: ¿los datos se contradicen entre si?
 *
 * Revisa DOS cosas distintas:
 *
 *   1. REFERENCIAS HUERFANAS. Un renglon que apunta a algo que ya no existe:
 *      una compra con un insumo borrado, una produccion de un platillo que se
 *      quito del menu. Cuando eso pasa, las pantallas muestran "(eliminado)" y
 *      los totales dejan de cuadrar sin explicacion visible.
 *
 *   2. EXISTENCIAS CONTRA EL LIBRO. Las cantidades guardadas en cada insumo son
 *      un acumulado en cache; el libro de movimientos es la fuente de verdad. Si
 *      difieren, hubo una escritura a medias.
 *
 * TODO ES SOLO LECTURA. No repara nada: reparar es una decision de la usuaria, y
 * ya existe `repararInventario` para el caso de las existencias.
 */

import { db } from './schema';
import { recalcularDesdeMovimientos, compararConLibro } from '@/domain/inventory';
import { estadoDe } from './repos/ingredients';
import type { Centavos } from '@/domain/money';

export type GravedadHallazgo = 'alerta' | 'aviso';

export interface Hallazgo {
  gravedad: GravedadHallazgo;
  /** Que se reviso, en palabras de la usuaria. */
  asunto: string;
  /** Que se encontro. */
  detalle: string;
  /** Cuantos registros estan afectados. */
  cuantos: number;
  /** Que puede hacer al respecto. */
  sugerencia: string;
}

export interface ReporteIntegridad {
  revisadoEn: string;
  /** true cuando no se encontro ningun problema. */
  sano: boolean;
  hallazgos: Hallazgo[];
  /** Cuantos registros se revisaron en total. */
  registrosRevisados: number;
  /** Insumos cuyas existencias no cuadran contra el libro. */
  descuadresDeInventario: Array<{
    nombre: string;
    stockGuardado: number;
    stockDelLibro: number;
    valorGuardado: Centavos;
    valorDelLibro: Centavos;
  }>;
}

/**
 * Revisa la base completa y devuelve lo que encontro.
 *
 * Se hace en una sola pasada leyendo todas las tablas: la base de un negocio
 * casero cabe de sobra en memoria, y hacerlo asi evita decenas de consultas.
 */
export async function verificarIntegridad(): Promise<ReporteIntegridad> {
  const [
    ingredientes,
    compras,
    renglonesCompra,
    recetas,
    renglonesReceta,
    plantillas,
    renglonesPlantilla,
    platillos,
    producciones,
    renglonesProduccion,
    ventas,
    destinos,
    movimientos,
    marcas,
    pedidos,
  ] = await Promise.all([
    db.ingredients.toArray(),
    db.purchases.toArray(),
    db.purchaseItems.toArray(),
    db.recipes.toArray(),
    db.recipeItems.toArray(),
    db.menuTemplates.toArray(),
    db.menuTemplateItems.toArray(),
    db.plannedItems.toArray(),
    db.productions.toArray(),
    db.productionItems.toArray(),
    db.sales.toArray(),
    db.saleDispositions.toArray(),
    db.inventoryMovements.toArray(),
    db.shoppingChecks.toArray(),
    db.orders.toArray(),
  ]);

  const idsInsumo = new Set(ingredientes.map((x) => x.id));
  const idsCompra = new Set(compras.map((x) => x.id));
  const idsReceta = new Set(recetas.map((x) => x.id));
  const idsPlantilla = new Set(plantillas.map((x) => x.id));
  const idsPlatillo = new Set(platillos.map((x) => x.id));
  const idsProduccion = new Set(producciones.map((x) => x.id));
  const idsVenta = new Set(ventas.map((x) => x.id));

  const hallazgos: Hallazgo[] = [];

  const revisar = (
    asunto: string,
    huerfanos: number,
    detalle: string,
    sugerencia: string,
    gravedad: GravedadHallazgo = 'alerta',
  ): void => {
    if (huerfanos > 0) {
      hallazgos.push({ gravedad, asunto, detalle, cuantos: huerfanos, sugerencia });
    }
  };

  // --- Referencias huerfanas ------------------------------------------
  revisar(
    'Renglones de compra',
    renglonesCompra.filter((x) => !idsCompra.has(x.purchaseId)).length,
    'Hay renglones que apuntan a una compra que ya no existe.',
    'Sus importes no van a aparecer en el historial de compras.',
  );

  revisar(
    'Renglones de compra',
    renglonesCompra.filter((x) => !idsInsumo.has(x.ingredientId)).length,
    'Hay renglones de compra de un insumo que ya no existe.',
    'Se van a ver como "(insumo eliminado)" en el historial.',
  );

  revisar(
    'Ingredientes de recetas',
    renglonesReceta.filter((x) => !idsReceta.has(x.recipeId)).length,
    'Hay ingredientes que pertenecen a una receta que ya no existe.',
    'No estorban, pero ocupan espacio. Se pueden limpiar restaurando un respaldo.',
    'aviso',
  );

  revisar(
    'Ingredientes de recetas',
    renglonesReceta.filter((x) => !idsInsumo.has(x.ingredientId)).length,
    'Hay recetas que piden un insumo que ya no existe.',
    'Su costo no se va a poder calcular y la receta va a salir incompleta.',
  );

  revisar(
    'Platillos del menú',
    platillos.filter((x) => !idsReceta.has(x.recipeId)).length,
    'Hay platillos del menú cuya receta ya no existe.',
    'No se va a poder saber qué cocinar ni cuánto cuesta.',
  );

  revisar(
    'Producciones',
    producciones.filter((x) => !idsPlatillo.has(x.plannedItemId)).length,
    'Hay producciones de un platillo que ya no está en el menú.',
    'Su costo sigue registrado, pero no se va a poder ligar a una venta.',
  );

  revisar(
    'Ingredientes de producción',
    renglonesProduccion.filter((x) => !idsProduccion.has(x.productionId)).length,
    'Hay renglones de una producción que ya no existe.',
    'No afectan el inventario, que se guía por el libro de movimientos.',
    'aviso',
  );

  revisar(
    'Ventas',
    ventas.filter((x) => x.plannedItemId !== null && !idsPlatillo.has(x.plannedItemId)).length,
    'Hay ventas de un platillo que ya no está en el menú.',
    'El ingreso sigue contando, pero su costo puede quedar sin ligar.',
  );

  revisar(
    'Ventas',
    ventas.filter((x) => !idsReceta.has(x.recipeId)).length,
    'Hay ventas de una receta que ya no existe.',
    'Se van a ver como "(receta eliminada)" en el historial.',
    'aviso',
  );

  revisar(
    'Ventas',
    ventas.filter((x) => x.productionId !== null && !idsProduccion.has(x.productionId)).length,
    'Hay ventas ligadas a una producción que ya no existe.',
    'Su costo quedó congelado en la venta, así que las ganancias siguen bien.',
    'aviso',
  );

  // --- Pedidos --------------------------------------------------------
  //
  // El pedido es un modulo aparte y no participa en costos ni inventario, asi
  // que un huerfano aqui no descuadra ningun numero del negocio: solo deja el
  // pedido sin poder mostrar de que platillo era.
  revisar(
    'Pedidos',
    pedidos.filter((x) => !idsReceta.has(x.recipeId)).length,
    'Hay pedidos de una receta que ya no existe.',
    'No se va a poder ver de qué platillo era el pedido. Los totales del día no se afectan.',
  );

  revisar(
    'Pedidos',
    pedidos.filter((x) => x.plannedItemId !== null && !idsPlatillo.has(x.plannedItemId)).length,
    'Hay pedidos ligados a un platillo del menú que ya no existe.',
    'El pedido sigue completo; solo pierde el enlace con el menú.',
    'aviso',
  );

  revisar(
    'Pedidos',
    pedidos.filter((x) => x.saleId !== null && !idsVenta.has(x.saleId)).length,
    'Hay pedidos ligados a una venta que ya no existe.',
    'El pedido sigue completo; solo pierde el enlace con la venta.',
    'aviso',
  );

  revisar(
    'Correcciones de venta',
    ventas.filter((x) => x.corrigeA !== null && !idsVenta.has(x.corrigeA)).length,
    'Hay correcciones que apuntan a una venta que ya no existe.',
    'La venta corregida podría contarse dos veces.',
  );

  revisar(
    'Destinos de sobrantes',
    destinos.filter((x) => !idsPlatillo.has(x.plannedItemId)).length,
    'Hay destinos de porciones no vendidas de un platillo que ya no existe.',
    'Su costo no se va a poder ubicar en ningún periodo.',
  );

  revisar(
    'Movimientos de inventario',
    movimientos.filter((x) => !idsInsumo.has(x.ingredientId)).length,
    'Hay movimientos de un insumo que ya no existe.',
    'El libro no va a cuadrar contra las existencias.',
  );

  revisar(
    'Plantilla semanal',
    renglonesPlantilla.filter((x) => !idsPlantilla.has(x.templateId) || !idsReceta.has(x.recipeId))
      .length,
    'La plantilla apunta a una receta o a una plantilla que ya no existe.',
    'Generar la semana desde la plantilla va a omitir esos días.',
    'aviso',
  );

  revisar(
    'Lista de compras',
    marcas.filter((x) => !idsInsumo.has(x.ingredientId)).length,
    'Hay marcas de la lista de compras de un insumo que ya no existe.',
    'No estorban.',
    'aviso',
  );

  // --- Existencias contra el libro ------------------------------------
  const porInsumo = new Map<string, typeof movimientos>();
  for (const movimiento of movimientos) {
    const previos = porInsumo.get(movimiento.ingredientId) ?? [];
    previos.push(movimiento);
    porInsumo.set(movimiento.ingredientId, previos);
  }

  const descuadresDeInventario: ReporteIntegridad['descuadresDeInventario'] = [];

  for (const insumo of ingredientes) {
    const suyos = porInsumo.get(insumo.id) ?? [];
    const delLibro = recalcularDesdeMovimientos(suyos, insumo.ultimoCostoPorBase);
    const guardado = estadoDe(insumo);

    /**
     * Se usa `compararConLibro` y NO una comparacion directa.
     *
     * Esa funcion redondea la diferencia antes de juzgarla, y ese detalle
     * importa: con cantidades fraccionarias (3272.727 g de carne al escalar una
     * receta de 11 porciones a 12) las sumas encadenadas dejan basura de punto
     * flotante en los ultimos bits. Comparar con `!==` reportaba descuadres que
     * no existen, y una herramienta que da falsas alarmas se deja de usar.
     */
    if (compararConLibro(guardado, delLibro).length > 0) {
      descuadresDeInventario.push({
        nombre: insumo.nombre,
        stockGuardado: guardado.stockBase,
        stockDelLibro: delLibro.stockBase,
        valorGuardado: guardado.valorCentavos,
        valorDelLibro: delLibro.valorCentavos,
      });
    }
  }

  if (descuadresDeInventario.length > 0) {
    hallazgos.push({
      gravedad: 'alerta',
      asunto: 'Existencias',
      detalle:
        `Las existencias guardadas de ${descuadresDeInventario.length} ` +
        `${descuadresDeInventario.length === 1 ? 'insumo' : 'insumos'} no coinciden con la ` +
        'suma de sus movimientos.',
      cuantos: descuadresDeInventario.length,
      sugerencia:
        'Puedes corregirlo desde Inventario → Verificación, que reconstruye las existencias ' +
        'desde el historial de movimientos.',
    });
  }

  const registrosRevisados =
    ingredientes.length +
    compras.length +
    renglonesCompra.length +
    recetas.length +
    renglonesReceta.length +
    plantillas.length +
    renglonesPlantilla.length +
    platillos.length +
    producciones.length +
    renglonesProduccion.length +
    ventas.length +
    destinos.length +
    movimientos.length +
    marcas.length +
    pedidos.length;

  return {
    revisadoEn: new Date().toISOString(),
    sano: hallazgos.length === 0,
    hallazgos,
    registrosRevisados,
    descuadresDeInventario,
  };
}
